import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import colorCodeApi, { F114 } from '../../../services/colorCodeApi';

type Mode = 'browse' | 'add' | 'edit';

export function useColorCodeForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F114[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [f114_01, setF11401] = useState('');
  const [f114_02, setF11402] = useState('');
  const [f114_11, setF11411] = useState('');
  const [f114_12, setF11412] = useState('');
  const [f114_14, setF11414] = useState(false);
  const [f114_15, setF11415] = useState('');
  const [f114_91, setF11491] = useState('');
  const [f114_92, setF11492] = useState<string | null>(null);
  const [f114_93, setF11493] = useState('');

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await colorCodeApi.list(1, 500);
      setRecords(res.data || []);
      if ((res.data || []).length > 0) {
        setCurrentIdx(0);
      }
    } catch {
      message.error('載入資料失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const syncFields = useCallback((rec: F114) => {
    setF11401(rec.f114_01 || '');
    setF11402(rec.f114_02 || '');
    setF11411(rec.f114_11 || '');
    setF11412(rec.f114_12 || '');
    setF11414(rec.f114_14 || false);
    setF11415(rec.f114_15 || '');
    setF11491(rec.f114_91 || '');
    setF11492(rec.f114_92);
    setF11493(rec.f114_93 || '');
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    if (records.length > 0 && currentIdx >= 0 && currentIdx < records.length) {
      syncFields(records[currentIdx]);
    }
  }, [records, currentIdx, syncFields]);

  // Navigation
  const navFirst = useCallback(() => setCurrentIdx(0), []);
  const navPrev = useCallback(() => setCurrentIdx((i) => Math.max(0, i - 1)), []);
  const navNext = useCallback(() => setCurrentIdx((i) => Math.min(records.length - 1, i + 1)), [records.length]);
  const navLast = useCallback(() => setCurrentIdx(records.length - 1), [records.length]);

  const startAdd = useCallback(() => {
    setMode('add');
    setF11401('');
    setF11402('');
    setF11411('');
    setF11412('');
    setF11414(false);
    setF11415('');
    setF11491('');
    setF11492(null);
    setF11493('');
  }, []);

  const startEdit = useCallback(() => {
    setMode('edit');
  }, []);

  const cancel = useCallback(() => {
    setMode('browse');
    if (records.length > 0) {
      syncFields(records[currentIdx]);
    }
  }, [records, currentIdx, syncFields]);

  const buildPayload = useCallback((): Partial<F114> => ({
    f114_01: f114_01.trim().toUpperCase(),
    f114_02: f114_02.trim(),
    f114_11: f114_11.trim(),
    f114_12: f114_12.trim(),
    f114_14: f114_14,
    f114_15: f114_15,
    f114_91: f114_91,
    f114_93: f114_93,
  }), [f114_01, f114_02, f114_11, f114_12, f114_14, f114_15, f114_91, f114_93]);

  const save = useCallback(async () => {
    setLoading(true);
    try {
      const payload = buildPayload();
      if (mode === 'add') {
        await colorCodeApi.create(payload);
        message.success('新增成功');
      } else {
        await colorCodeApi.update(f114_01, payload);
        message.success('儲存成功');
      }
      setMode('browse');
      await loadRecords();
    } catch (err: any) {
      if (err?.response?.status === 422) {
        const errors = err.response.data?.errors;
        if (Array.isArray(errors) && errors.length > 0) {
          message.error(errors[0].message);
        }
      } else {
        message.error('儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [mode, f114_01, buildPayload, loadRecords]);

  const doDelete = useCallback(async () => {
    if (!f114_01) return;
    setLoading(true);
    try {
      await colorCodeApi.delete(f114_01);
      message.success('刪除成功');
      await loadRecords();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [f114_01, loadRecords]);

  const quickSearch = useCallback((keyword: string) => {
    const idx = records.findIndex((r) => r.f114_01.toUpperCase().startsWith(keyword.toUpperCase()));
    if (idx >= 0) {
      setCurrentIdx(idx);
    } else {
      message.warning('查無資料');
    }
  }, [records]);

  return {
    mode, loading, records, currentIdx,
    f114_01, f114_02, f114_11, f114_12, f114_14, f114_15,
    f114_91, f114_92, f114_93,
    setF11401, setF11411, setF11412, setF11414, setF11415,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete,
    quickSearch,
    recordCount: records.length,
    recordPosition: records.length > 0 ? currentIdx + 1 : 0,
  };
}
